"use client";

import { useState, useEffect } from "react";
import { usePeriods } from "@/services/usePeriods";
import { PeriodSummary } from "../admin/reports/PeriodSummary";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CalendarDays, AlertCircle, Package, CheckCircle2, Info } from "lucide-react";

// --- Types ---
type UserShape = {
    participant_id: number;
    role_id: number;
};

type ActivePeriodShape = {
    id: number;
};

type Props = {
    user: UserShape;
    activePeriod?: ActivePeriodShape | null;
};

export default function AdminPackage({ activePeriod }: Props) {
    const { data, isLoading, isError } = usePeriods();

    // State period aktif — inisialisasi lazy dari data yang mungkin sudah tersedia di
    // cache React Query (mis. saat berpindah tab lalu kembali), supaya tidak ada jeda
    // "Belum ada periode dipilih" sebelum useEffect di bawah sempat menyusulkan nilainya.
    const [periodId, setPeriodId] = useState<string>(() => {
        if (activePeriod?.id) return activePeriod.id.toString();
        const flaggedActive = data?.find((p) => p.is_active);
        if (flaggedActive) return flaggedActive.period_id.toString();
        return data?.[0]?.period_id?.toString() ?? "";
    });

    useEffect(() => {
        if (periodId || !data || data.length === 0) return;

        // Prioritas 1: periode aktif dari useActivePeriod (aktif & tanggal hari ini masuk rentang)
        if (activePeriod?.id) {
            setPeriodId(activePeriod.id.toString());
            return;
        }

        // Prioritas 2: periode yang ditandai is_active di daftar periode
        const flaggedActive = data.find((p) => p.is_active);
        if (flaggedActive) {
            setPeriodId(flaggedActive.period_id.toString());
            return;
        }

        // Fallback: periode terbaru (data sudah terurut desc by start_date dari backend)
        setPeriodId(data[0].period_id.toString());
    }, [data, periodId, activePeriod]);

    // --- 1. Loading State ---
    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto space-y-8 px-4 md:px-0 animate-in fade-in duration-500">
                <div className="flex flex-col md:flex-row justify-between gap-4 p-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800">
                    <div className="space-y-3">
                        <Skeleton className="h-8 w-[250px] rounded-lg" />
                        <Skeleton className="h-4 w-[350px] rounded-md" />
                    </div>
                    <Skeleton className="h-12 w-[200px] rounded-xl" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-[88px] w-full rounded-2xl" />)}
                </div>
            </div>
        );
    }

    // --- 2. Error State ---
    if (isError) {
        return (
            <div className="max-w-7xl mx-auto px-4 md:px-0">
                <Alert variant="destructive" className="my-6 rounded-2xl border-rose-200 bg-rose-50 text-rose-800 dark:bg-rose-900/20 dark:border-rose-900/50 dark:text-rose-400">
                    <AlertCircle className="h-5 w-5" />
                    <AlertTitle className="font-bold">Terjadi Kesalahan</AlertTitle>
                    <AlertDescription>
                        Gagal memuat daftar periode. Silakan muat ulang halaman.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">

            {/* --- HEADER SECTION (Sesuai Tema) --- */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                            <Package className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                            Laporan & Paket Konaspi
                        </h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Rekapitulasi data pendaftar, distribusi paket, dan monitoring status pembayaran berdasarkan periode kegiatan.
                    </p>
                </div>

                {/* Filter Dropdown Periode */}
                <div className="bg-slate-50 dark:bg-slate-800/50 px-4 py-2 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center gap-3 w-full md:w-80 shrink-0">
                    <CalendarDays className="w-5 h-5 text-slate-400 shrink-0 hidden sm:block" />
                    <Select value={periodId} onValueChange={(value) => setPeriodId(value)}>
                        <SelectTrigger className="w-full bg-transparent border-0 shadow-none focus:ring-0 text-sm font-bold text-slate-700 dark:text-slate-300 outline-none cursor-pointer p-0 h-auto min-w-0">
                            <div className="truncate w-full text-left">
                                <SelectValue placeholder="Pilih Periode Laporan" />
                            </div>
                        </SelectTrigger>
                        <SelectContent align="end" className="rounded-xl border-slate-200 dark:border-slate-700 shadow-xl max-w-[90vw] sm:max-w-md">
                            {data?.map((period) => (
                                <SelectItem
                                    key={period.period_id}
                                    value={period.period_id.toString()}
                                    className="cursor-pointer py-2.5 px-3 rounded-lg focus:bg-slate-100 dark:focus:bg-slate-800"
                                >
                                    <div className="flex items-start justify-between w-full gap-4 pr-2">
                                        <span className="font-semibold whitespace-normal break-words leading-snug text-left">{period.name}</span>
                                        {period.is_active && (
                                            <Badge variant="secondary" className="h-5 px-2 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 border-0 text-[10px] gap-1 shrink-0 mt-0.5">
                                                <CheckCircle2 className="w-3 h-3" /> Aktif
                                            </Badge>
                                        )}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* --- CONTENT SUMMARY --- */}
            <div>
                {periodId ? (
                    <PeriodSummary periodId={periodId} />
                ) : (
                    // Blank State jika belum memilih periode
                    <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 bg-white dark:bg-slate-900 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
                        <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-full">
                            <Info className="w-8 h-8 text-slate-400" />
                        </div>
                        <div className="space-y-1">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Belum ada periode dipilih</h3>
                            <p className="text-sm text-slate-500 max-w-sm mx-auto">
                                Silakan pilih periode pada menu *dropdown* di atas untuk mulai melihat laporan dan metrik pendaftaran.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}